import { BrandTheme, BrandThemeV3 } from '../types/theme.v3';
import { BrandThemeV2 } from '../types/theme.v2';
import { zenPacificThemeV3 } from './zenPacificTheme.v3';

const createContentTypes = (primary: string, secondary: string) => ({
  text: {
    color: "#1e293b",
    fontSize: "15px",
    lineHeight: "1.6"
  },
  highlight: {
    background: `${primary}10`,
    border: `${primary}30`,
    borderLeft: primary,
    text: primary,
    icon: "🔒",
    padding: "14px 16px",
    borderRadius: "8px"
  },
  feature: {
    background: "#fffbeb",
    border: "#fed7aa",
    borderLeft: "#f59e0b",
    text: "#92400e",
    icon: "⭐",
    padding: "14px 16px",
    borderRadius: "8px"
  },
  warning: {
    background: "#fef2f2",
    border: "#fecaca",
    borderLeft: "#ef4444",
    text: "#991b1b",
    icon: "⚠️",
    padding: "14px 16px",
    borderRadius: "8px"
  },
  success: {
    background: "#f0fdf4",
    border: "#bbf7d0",
    borderLeft: "#22c55e",
    text: "#166534",
    icon: "✅",
    padding: "14px 16px",
    borderRadius: "8px"
  },
  details: {
    background: "#f8fafc",
    border: "#e5e7eb",
    borderRadius: "8px",
    title: {
      background: "#f8fafc",
      text: "#1e293b",
      fontSize: "14px",
      fontWeight: "600",
      padding: "12px 16px",
      borderBottom: "#e5e7eb"
    },
    content: {
      background: "#ffffff",
      text: "#1e293b",
      fontSize: "14px",
      lineHeight: "1.7",
      padding: "16px"
    }
  },
  image: {
    borderRadius: "12px",
    shadow: `0 4px 12px ${primary}20`,
    caption: {
      background: "#f8fafc",
      text: "#475569",
      fontSize: "13px",
      padding: "12px",
      borderTop: "#e5e7eb"
    }
  },
  weather: {
    background: "#fffbeb",
    border: "#fed7aa",
    borderLeft: "#f59e0b",
    text: "#92400e",
    icon: "☀️",
    padding: "16px",
    borderRadius: "8px"
  },
  video: {
    borderRadius: "12px",
    shadow: `0 4px 12px ${primary}20`,
    background: "#000000",
    border: "#e5e7eb",
    controls: {
      background: "#000000cc",
      color: "#ffffff"
    },
    caption: {
      background: "#f8fafc",
      text: "#475569",
      fontSize: "13px",
      padding: "12px",
      borderTop: "#e5e7eb"
    }
  },
  chips: {
    background: "#f3f4f6",
    text: "#374151",
    border: primary,
    padding: "10px 16px",
    borderRadius: "12px",
    fontSize: "13px",
    minHeight: "44px",
    hover: {
      background: primary,
      text: "#ffffff",
      border: primary,
      shadow: `0 4px 8px ${primary}50`
    }
  }
});

export const BRAND_THEMES: Record<string, BrandTheme> = {
  'zen-pacific-orlando': zenPacificThemeV3,
  'zen-pacific-orlando-v2': {
    _meta: {
      version: "2.0",
      description: "Zen Pacific Orlando - Luxury boutique hotel theme",
      wcagTarget: "AA",
      generatedFor: "Zen Pacific Orlando"
    },
    brand: {
      name: "Zen Pacific",
      fonts: {
        primary: "Playfair Display, Georgia, 'Times New Roman', serif",
        secondary: "system-ui, -apple-system, 'Segoe UI', sans-serif"
      },
      colors: {
        primary: "#4C0D16",
        secondary: "#8B1538",
        cta: "#AC7F42",
        ctaText: "#6E5028",
        accent: "#AC7F42",  // Backward compatibility (alias for cta)
        inactive: "#9CA3AF",  // Backward compatibility
        border: "#E0E0E0",  // Backward compatibility (use surface.border)
        primaryDark: "#3A0A11",  // Backward compatibility
        primaryDarker: "#2A0708",  // Backward compatibility
        text: {
          primary: "#1A1A1A",
          secondary: "#4A4A4A",
          light: "#545454",
          disabled: "#736960",
          placeholder: "#6E6E6E"  // Backward compatibility
        },
        gradient: {
          start: "#4C0D16",
          middle: "#8B1538",
          end: "#4C0D16"
        },
        surface: {
          base: "#FFFFFF",
          subtle: "#FAFAFA",
          warm: "#FDF6F0",
          hover: "#FAF6F0",
          border: "#E0E0E0",
          borderSubtle: "#F0F0F0"
        }
      },
      shadows: {
        light: "0 4px 12px rgba(76, 13, 22, 0.12)",
        medium: "0 8px 24px rgba(76, 13, 22, 0.15)",
        aiCard: "0 8px 24px rgba(76, 13, 22, 0.08)",  // Backward compatibility
        focus: "0 0 0 3px rgba(172, 127, 66, 0.15)"  // Backward compatibility
      },
      // Backward compatibility - mirror transactionalUI values
      buttons: {
        primary: {
          text: "#FFFFFF",
          background: "#4C0D16",
          hoverBackground: "#3A0A11",
          border: "none",
          padding: "14px 32px",
          fontSize: "14px",
          fontWeight: "500",
          borderRadius: "0",
          letterSpacing: "0.08em"
        },
        secondary: {
          text: "#4C0D16",
          background: "transparent",
          hoverBackground: "#FDF6F0",
          border: "1px solid #4C0D16",
          padding: "14px 32px",
          fontSize: "14px",
          fontWeight: "500",
          borderRadius: "0",
          letterSpacing: "0.08em"
        },
        cta: {
          text: "#FFFFFF",
          background: "#AC7F42",
          hoverBackground: "#8B6634",
          border: "none",
          padding: "14px 32px",
          fontSize: "14px",
          fontWeight: "600",
          borderRadius: "0",
          minFontSize: "16px"
        }
      },
      chips: {
        text: "#FFFFFF",
        background: "#4A4A4A",
        hoverText: "#FFFFFF",
        hoverBackground: "#4A4A4ACC",
        hoverBorder: "#4A4A4A",
        border: "#4A4A4A",
        padding: "10px 16px",
        fontSize: "13px",
        minHeight: "44px",
        borderRadius: "12px"
      },
      formInputs: {
        border: "1px solid #E0E0E0",
        background: "#FFFFFF",
        focusBorder: "#AC7F42",
        focusShadow: "0 0 0 3px rgba(172, 127, 66, 0.15)",
        text: "#1A1A1A",
        placeholder: "#6E6E6E",
        fontSize: "15px",
        padding: "14px 16px",
        borderRadius: "0"
      },
      calendar: {
        background: "#FFFFFF",
        border: "1px solid #E0E0E0",
        borderRadius: "0",
        padding: "32px 28px",
        shadow: "0 4px 20px rgba(0,0,0,0.06)",
        month: {
          font: "Playfair Display, Georgia, 'Times New Roman', serif",
          fontSize: "18px",
          fontWeight: "500",
          color: "#4A4A4A"
        },
        daySelected: {
          color: "#6E5028",
          background: "#FFFFFF",
          border: "1.5px solid #AC7F42"
        }
      },
      pricing: {
        amount: {
          font: "Playfair Display, Georgia, 'Times New Roman', serif",
          fontSize: "28px",
          fontWeight: "500",
          color: "#1A1A1A"
        },
        currency: {
          fontSize: "16px",
          fontWeight: "400",
          color: "#4A4A4A"
        },
        total: {
          fontSize: "32px",
          fontWeight: "600",
          color: "#4C0D16"
        },
        strikethrough: {
          color: "#736960"
        },
        savings: {
          color: "#2D5A3D",
          background: "#EBF8FF",
          fontSize: "12px",
          fontWeight: "500"
        }
      },
      badges: {
        text: "#AC7F42",
        background: "transparent",
        border: "1px solid #AC7F42",
        fontSize: "10px",
        fontWeight: "500",
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        padding: "3px 10px"
      },
      reservationCard: {
        imageBorderRadius: "0",
        imageOverlay: "linear-gradient(180deg, transparent 60%, rgba(0,0,0,0.4) 100%)",
        badge: {
          text: "#FFFFFF",
          background: "rgba(76, 13, 22, 0.85)",
          fontSize: "10px",
          fontWeight: "500",
          letterSpacing: "0.12em",
          padding: "6px 12px"
        },
        confirmation: {
          font: "system-ui, -apple-system, 'Segoe UI', sans-serif",
          fontSize: "11px",
          color: "#4A4A4A"
        },
        daterange: {
          font: "Playfair Display, Georgia, 'Times New Roman', serif",
          fontSize: "20px",
          color: "#1A1A1A"
        }
      },
      table: {
        header: {
          font: "system-ui, -apple-system, 'Segoe UI', sans-serif",
          fontSize: "10px",
          fontWeight: "500",
          color: "#4A4A4A",
          textTransform: "uppercase",
          letterSpacing: "0.12em",
          borderBottom: "1px solid #E0E0E0",
          padding: "12px 16px"
        },
        cell: {
          font: "system-ui, -apple-system, 'Segoe UI', sans-serif",
          fontSize: "14px",
          color: "#1A1A1A",
          padding: "14px 16px",
          borderBottom: "1px solid #F0F0F0"
        },
        rowHover: "#FAF6F0",
        stripe: "transparent"
      },
      progressStepper: {
        active: {
          text: "#FFFFFF",
          background: "#4C0D16",
          border: "#4C0D16"
        },
        completed: {
          text: "#FFFFFF",
          background: "#AC7F42",
          border: "#AC7F42",
          check: "#FFFFFF"
        },
        pending: {
          text: "#4A4A4A",
          background: "transparent",
          border: "#E0E0E0"
        },
        connector: {
          color: "#E0E0E0",
          completed: "#AC7F42",
          width: "1px"
        },
        label: {
          font: "system-ui, -apple-system, 'Segoe UI', sans-serif",
          fontSize: "11px",
          fontWeight: "500",
          color: "#4A4A4A",
          activeColor: "#1A1A1A"
        }
      }
    },
    accessibility: {
      focusOutline: "#AC7F42",
      focusOutlineWidth: "3px",
      focusOutlineOffset: "2px",
      minTouchTarget: "44px",
      contrastRequirements: {
        normalText: 4.5,
        largeText: 3.0,
        inactiveComponents: 3.0,
        decorativeElements: "No requirement"
      }
    },
    transactionalUI: {
      card: {
        background: "#FFFFFF",
        border: "1px solid #E0E0E0",
        borderRadius: "0",
        shadow: "0 4px 12px rgba(76, 13, 22, 0.12)",
        padding: "40px 44px 36px",
        cornerAccent: {
          enabled: true,
          color: "#AC7F42",
          size: "22px",
          offset: "10px",
          borderWidth: "1px"
        }
      },
      sectionHeading: {
        fontFamily: "Playfair Display, Georgia, 'Times New Roman', serif",
        fontSize: "13px",
        fontWeight: "400",
        color: "#4A4A4A",
        textTransform: "uppercase",
        letterSpacing: "0.22em",
        decorativeRule: {
          enabled: true,
          lineWidth: "50px",
          lineColor: "linear-gradient(90deg, transparent, #AC7F42, transparent)",
          diamondSize: "5px",
          diamondColor: "#AC7F42"
        }
      },
      dataLabel: {
        fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
        fontSize: "10px",
        fontWeight: "500",
        color: "#4A4A4A",
        textTransform: "uppercase",
        letterSpacing: "0.12em"
      },
      dataValue: {
        fontFamily: "Playfair Display, Georgia, 'Times New Roman', serif",
        fontSize: "20px",
        fontWeight: "400",
        color: "#1A1A1A",
        variants: {
          highlighted: {
            color: "#6E5028",
            fontSize: "30px",
            fontWeight: "500"
          },
          emphasis: {
            color: "#4C0D16",
            fontWeight: "500"
          }
        }
      },
      divider: {
        vertical: {
          width: "1px",
          color: "linear-gradient(180deg, transparent, #AC7F42 25%, #AC7F42 75%, transparent)"
        },
        horizontal: {
          width: "1px",
          color: "#F0F0F0"
        },
        accent: {
          width: "60px",
          height: "1px",
          color: "#AC7F42"
        }
      },
      infoBanner: {
        background: "#FDF6F0",
        border: "1px solid #FAE8D8",
        borderRadius: "0",
        headingColor: "#6E5028",
        bodyColor: "#4A4A4A",
        iconColor: "#AC7F42",
        iconType: "info-circle"
      },
      progressStepper: {
        activeStep: {
          textColor: "#FFFFFF",
          background: "#4C0D16",
          borderColor: "#4C0D16"
        },
        completedStep: {
          textColor: "#FFFFFF",
          background: "#AC7F42",
          borderColor: "#AC7F42",
          checkColor: "#FFFFFF"
        },
        pendingStep: {
          textColor: "#4A4A4A",
          background: "transparent",
          borderColor: "#E0E0E0"
        },
        connector: {
          color: "#E0E0E0",
          completedColor: "#AC7F42",
          width: "1px"
        },
        label: {
          fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
          fontSize: "11px",
          fontWeight: "500",
          color: "#4A4A4A",
          activeColor: "#1A1A1A"
        }
      },
      button: {
        primary: {
          text: "#FFFFFF",
          background: "#4C0D16",
          border: "none",
          hoverBackground: "#3A0A11",
          fontSize: "14px",
          fontWeight: "500",
          padding: "14px 32px",
          borderRadius: "0",
          letterSpacing: "0.08em"
        },
        secondary: {
          text: "#4C0D16",
          background: "transparent",
          border: "1px solid #4C0D16",
          hoverBackground: "#FDF6F0",
          fontSize: "14px",
          fontWeight: "500",
          padding: "14px 32px",
          borderRadius: "0",
          letterSpacing: "0.08em"
        },
        cta: {
          text: "#FFFFFF",
          background: "#AC7F42",
          border: "none",
          hoverBackground: "#8B6634",
          fontSize: "14px",
          fontWeight: "600",
          padding: "14px 32px",
          borderRadius: "0",
          minFontSize: "16px"
        },
        ctaDarkText: {
          text: "#6E5028",
          background: "#FDF6F0",
          border: "1px solid #AC7F42",
          hoverBackground: "#FAF0E0",
          fontSize: "14px",
          fontWeight: "600"
        }
      },
      memberBadge: {
        text: "#AC7F42",
        background: "transparent",
        border: "1px solid #AC7F42",
        fontSize: "10px",
        fontWeight: "500",
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        padding: "3px 10px"
      },
      reservationCard: {
        image: {
          borderRadius: "0",
          overlay: "linear-gradient(180deg, transparent 60%, rgba(0,0,0,0.4) 100%)"
        },
        stayBadge: {
          text: "#FFFFFF",
          background: "rgba(76, 13, 22, 0.85)",
          fontSize: "10px",
          fontWeight: "500",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          padding: "6px 12px"
        },
        confirmationNumber: {
          fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
          fontSize: "11px",
          color: "#4A4A4A",
          letterSpacing: "0.02em"
        },
        dateRange: {
          fontFamily: "Playfair Display, Georgia, 'Times New Roman', serif",
          fontSize: "20px",
          color: "#1A1A1A",
          fontWeight: "400"
        }
      }
    },
    calendar: {
      container: {
        background: "#FFFFFF",
        border: "1px solid #E0E0E0",
        borderRadius: "0",
        padding: "32px 28px",
        shadow: "0 4px 20px rgba(0,0,0,0.06)"
      },
      monthHeading: {
        fontFamily: "Playfair Display, Georgia, 'Times New Roman', serif",
        fontSize: "18px",
        fontWeight: "500",
        color: "#4A4A4A",
        letterSpacing: "0.02em"
      },
      navigation: {
        color: "#AC7F42",
        hoverColor: "#6E5028",
        size: "20px",
        style: "minimal"
      },
      dayOfWeekHeader: {
        fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
        fontSize: "10px",
        fontWeight: "600",
        color: "#4A4A4A",
        textTransform: "uppercase",
        letterSpacing: "0.12em"
      },
      dayCell: {
        default: {
          fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
          fontSize: "14px",
          fontWeight: "400",
          color: "#1A1A1A",
          background: "#FFFFFF",
          border: "1px solid transparent",
          hoverBackground: "#FAF6F0"
        },
        disabled: {
          color: "#CCCCCC",
          background: "#FAFAFA",
          cursor: "not-allowed"
        },
        currentCheckout: {
          color: "#6E5028",
          background: "#FFFFFF",
          fontWeight: "600",
          hoverBackground: "#FDF6F0",
          subLabel: {
            color: "#AC7F42",
            fontSize: "9px",
            fontWeight: "600",
            textTransform: "uppercase",
            letterSpacing: "0.08em"
          }
        },
        selected: {
          color: "#6E5028",
          background: "#FFFFFF",
          border: "1.5px solid #AC7F42",
          fontWeight: "500",
          hoverBackground: "#FDF6F0"
        },
        today: {
          indicator: {
            type: "dot",
            size: "4px",
            color: "#AC7F42"
          }
        },
        range: {
          background: "#FDF6F0",
          startEnd: {
            background: "#FAE8D8",
            color: "#6E5028"
          }
        }
      }
    },
    forms: {
      dateSelector: {
        border: "1px solid #E0E0E0",
        background: "#FFFFFF",
        padding: "14px 16px",
        textColor: "#1A1A1A",
        fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
        fontSize: "15px",
        fontWeight: "400",
        hover: {
          background: "#FAF6F0",
          borderColor: "#AC7F42"
        },
        chevron: {
          color: "#4A4A4A",
          position: "right"
        }
      },
      textInput: {
        border: "1px solid #E0E0E0",
        background: "#FFFFFF",
        focusBorder: "#AC7F42",
        focusShadow: "0 0 0 3px rgba(172, 127, 66, 0.15)",
        textColor: "#1A1A1A",
        placeholderColor: "#6E6E6E",
        fontSize: "15px",
        padding: "14px 16px",
        borderRadius: "0"
      },
      select: {
        border: "1px solid #E0E0E0",
        background: "#FFFFFF",
        textColor: "#1A1A1A",
        chevronColor: "#4A4A4A",
        focusBorder: "#AC7F42",
        optionHover: "#FAF6F0"
      },
      checkbox: {
        border: "1px solid #E0E0E0",
        checkedBackground: "#AC7F42",
        checkedBorder: "#AC7F42",
        checkColor: "#FFFFFF",
        size: "18px"
      }
    },
    table: {
      header: {
        fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
        fontSize: "10px",
        fontWeight: "500",
        color: "#4A4A4A",
        textTransform: "uppercase",
        letterSpacing: "0.12em",
        borderBottom: "1px solid #E0E0E0",
        padding: "12px 16px"
      },
      cell: {
        fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
        fontSize: "14px",
        color: "#1A1A1A",
        padding: "14px 16px",
        borderBottom: "1px solid #F0F0F0"
      },
      rowHover: "#FAF6F0",
      stripe: "transparent"
    },
    pricing: {
      amount: {
        fontFamily: "Playfair Display, Georgia, 'Times New Roman', serif",
        fontSize: "28px",
        fontWeight: "500",
        color: "#1A1A1A"
      },
      currency: {
        fontSize: "16px",
        fontWeight: "400",
        color: "#4A4A4A"
      },
      perUnit: {
        fontSize: "12px",
        color: "#4A4A4A"
      },
      total: {
        fontSize: "32px",
        fontWeight: "600",
        color: "#4C0D16"
      },
      strikethrough: {
        color: "#736960",
        textDecoration: "line-through"
      },
      savings: {
        color: "#2D5A3D",
        background: "#EBF8FF",
        fontSize: "12px",
        fontWeight: "500"
      }
    },
    aiAssistant: {
      name: "Sara",
      logoUrl: "https://example.com/sara-logo.png",
      avatar: {
        size: "36px",
        border: "2px solid #AC7F42",
        shadow: "0 4px 12px rgba(172, 127, 66, 0.25)",
        borderRadius: "50%",
        fallback: {
          letter: "S",
          backgroundColor: "#4C0D16"
        },
        gradient: {
          start: "#4C0D16",
          end: "#AC7F42"
        }
      },
      nameStyle: {
        color: "#4C0D16",
        fontSize: "15px",
        fontWeight: "600"
      },
      timestamp: {
        color: "#6E6E6E",
        fontSize: "12px"
      }
    },
    userInterface: {
      topBar: {
        background: "linear-gradient(135deg, #4C0D16 0%, #8B1538 100%)",
        text: "#FFFFFF",
        borderBottom: "#AC7F42"
      },
      userMessage: {
        background: "#4C0D16",
        text: "#FFFFFF",
        border: "#E0E0E0",
        shadow: "0 8px 24px rgba(76, 13, 22, 0.08)"
      },
      inputBox: {
        inactive: {
          background: "#FFFFFF",
          text: "#1A1A1A",
          border: "#E0E0E0",
          placeholder: "#6E6E6E"
        },
        active: {
          background: "#FFFFFF",
          text: "#1A1A1A",
          border: "#AC7F42",
          shadow: "0 0 0 3px rgba(172, 127, 66, 0.15)"
        }
      },
      sendButton: {
        background: "#AC7F42",
        text: "#FFFFFF",
        border: "#AC7F42",
        shadow: "0 2px 8px rgba(172, 127, 66, 0.2)",
        hover: {
          background: "#8B6634",
          text: "#FFFFFF",
          border: "#8B6634",
          shadow: "0 4px 12px rgba(172, 127, 66, 0.3)"
        },
        disabled: {
          background: "#9CA3AF",
          border: "#9CA3AF"
        }
      }
    },
    aiResponseSystem: {
      responseCard: {
        background: "#FFFFFF",
        border: "#F0F0F0",
        shadow: "0 8px 24px rgba(76, 13, 22, 0.08)",
        borderRadius: "12px"
      },
      avatar: {
        size: "36px",
        borderRadius: "50%",
        border: "2px solid #AC7F42",
        shadow: "0 4px 12px rgba(172, 127, 66, 0.25)",
        image: {
          width: "100%",
          height: "100%",
          objectFit: "cover"
        }
      },
      contentTypes: {
        text: {
          color: "#1A1A1A",
          fontSize: "15px",
          lineHeight: "1.6"
        },
        highlight: {
          background: "#FDF6F0",
          border: "#FAE8D8",
          borderLeft: "3px solid #AC7F42",
          text: "#6E5028",
          icon: "#AC7F42",
          padding: "14px 16px",
          borderRadius: "0"
        },
        feature: {
          background: "#FFFBEB",
          border: "#FED7AA",
          borderLeft: "3px solid #F59E0B",
          text: "#92400E",
          icon: "#F59E0B",
          padding: "14px 16px",
          borderRadius: "0"
        },
        warning: {
          background: "#FEF2F2",
          border: "#FECACA",
          borderLeft: "3px solid #EF4444",
          text: "#991B1B",
          icon: "#EF4444",
          padding: "14px 16px",
          borderRadius: "0"
        },
        success: {
          background: "#F0FDF4",
          border: "#BBF7D0",
          borderLeft: "3px solid #22C55E",
          text: "#166534",
          icon: "#22C55E",
          padding: "14px 16px",
          borderRadius: "0"
        },
        details: {
          background: "#F8FAFC",
          border: "#E5E7EB",
          borderRadius: "0",
          title: {
            background: "#F8FAFC",
            text: "#1A1A1A",
            fontSize: "14px",
            fontWeight: "600",
            padding: "12px 16px",
            borderBottom: "#E5E7EB"
          },
          content: {
            background: "#FFFFFF",
            text: "#1A1A1A",
            fontSize: "14px",
            lineHeight: "1.7",
            padding: "16px"
          }
        },
        image: {
          borderRadius: "0",
          shadow: "0 4px 12px rgba(76, 13, 22, 0.12)",
          caption: {
            background: "#F8FAFC",
            text: "#475569",
            fontSize: "13px",
            padding: "12px",
            borderTop: "#E5E7EB"
          }
        },
        chips: {
          text: "#FFFFFF",
          background: "#4A4A4A",
          border: "#4A4A4A",
          padding: "10px 16px",
          fontSize: "13px",
          minHeight: "44px",
          borderRadius: "12px",
          hover: {
            background: "#4A4A4ACC",
            text: "#FFFFFF",
            border: "#4A4A4A",
            shadow: "0 4px 8px rgba(74, 74, 74, 0.3)"
          }
        }
      }
    }
  } as BrandThemeV2,

  'hyatt': {
    brand: {
      name: "Hyatt",
      colors: {
        primary: "#1e3a8a",
        secondary: "#3b82f6",
        cta: "#f59e0b",
        ctaText: "#b45309",
        gradient: {
          start: "#f5f7fa",
          middle: "#cfdee9",
          end: "#f5f7fa"
        },
        text: {
          primary: "#1e293b",
          secondary: "#475569",
          light: "#94a3b8",
          disabled: "#9ca3af"
        },
        surface: {
          base: "#FFFFFF",
          subtle: "#FAFAFA",
          warm: "#FEF3E2",
          hover: "#FEF7ED",
          border: "#E0E0E0",
          borderSubtle: "#F0F0F0"
        }
      },
      fonts: {
        primary: "Inter, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, sans-serif",
        secondary: "system-ui, -apple-system, 'Segoe UI', sans-serif"
      },
      shadows: {
        light: "0 2px 8px rgba(30, 58, 138, 0.15)",
        medium: "0 4px 16px rgba(30, 58, 138, 0.1)"
      }
    },
    userInterface: {
      topBar: {
        background: "#1e3a8a",
        text: "#ffffff",
        borderBottom: "#1e40af"
      },
      userMessage: {
        background: "#1e3a8a",
        text: "#ffffff",
        border: "#3b82f6",
        shadow: "0 2px 8px rgba(59, 130, 246, 0.15)"
      },
      inputBox: {
        inactive: {
          background: "#f8fafc",
          text: "#1e293b",
          border: "#d1d5db",
          placeholder: "#6b7280"
        },
        active: {
          background: "#ffffff",
          text: "#1e293b",
          border: "#3b82f6",
          shadow: "0 0 0 3px rgba(59, 130, 246, 0.1)"
        }
      },
      sendButton: {
        background: "#f59e0b",
        text: "#ffffff",
        border: "#f59e0b",
        shadow: "0 2px 8px rgba(245, 158, 11, 0.2)",
        hover: {
          background: "#d97706",
          text: "#ffffff",
          border: "#d97706",
          shadow: "0 4px 12px rgba(245, 158, 11, 0.3)"
        },
        disabled: {
          background: "#9ca3af",
          text: "#ffffff",
          border: "#9ca3af"
        }
      }
    },
    aiResponseSystem: {
      responseCard: {
        background: "#ffffff",
        border: "#e5e7eb",
        shadow: "0 4px 16px rgba(30, 58, 138, 0.1)",
        borderRadius: "8px",
        padding: {
          default: "32px",
          large: "40px"
        }
      },
      avatar: {
        size: "40px",
        borderRadius: "50%",
        shadow: "0 2px 8px rgba(30, 58, 138, 0.3)",
        border: "none",
        image: {
          objectFit: "cover",
          width: "100%",
          height: "100%"
        }
      },
      contentTypes: createContentTypes("#1e3a8a", "#3b82f6")
    },
    aiAssistant: {
      name: "Emma",
      avatar: {
        size: "32px",
        gradient: {
          start: "#3B82F6",
          end: "#1E3A8A"
        },
        fallback: {
          letter: "E",
          backgroundColor: "#1E3A8A"
        },
        border: "2px solid #FFFFFF",
        shadow: "0 2px 8px rgba(59, 130, 246, 0.2)"
      },
      nameStyle: {
        color: "#1E293B",
        fontWeight: "700",
        fontSize: "14px"
      },
      timestamp: {
        color: "#94A3B8",
        fontSize: "12px"
      }
    },
    accessibility: {
      focusOutline: "#fbbf24",
      focusOutlineWidth: "3px",
      focusOutlineOffset: "2px",
      minTouchTarget: "44px"
    }
  } as BrandThemeV2,

  'marriott': {
    brand: {
      name: "Marriott",
      colors: {
        primary: "#1f2937",
        secondary: "#6b7280",
        cta: "#c7934d",
        ctaText: "#92681f",
        gradient: {
          start: "#f9fafb",
          middle: "#e5e7eb",
          end: "#f9fafb"
        },
        text: {
          primary: "#1f2937",
          secondary: "#4b5563",
          light: "#6b7280",
          disabled: "#9ca3af"
        },
        surface: {
          base: "#FFFFFF",
          subtle: "#FAFAFA",
          warm: "#FEF3E2",
          hover: "#FEF7ED",
          border: "#E0E0E0",
          borderSubtle: "#F0F0F0"
        }
      },
      fonts: {
        primary: "-apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, \"Helvetica Neue\", Arial, sans-serif",
        secondary: "system-ui, -apple-system, 'Segoe UI', sans-serif"
      },
      shadows: {
        light: "0 2px 8px rgba(31, 41, 55, 0.08)",
        medium: "0 4px 16px rgba(31, 41, 55, 0.06)"
      }
    },
    userInterface: {
      topBar: {
        background: "#1f2937",
        text: "#ffffff",
        borderBottom: "#374151"
      },
      userMessage: {
        background: "#1f2937",
        text: "#ffffff",
        border: "#e5e7eb",
        shadow: "0 2px 8px rgba(31, 41, 55, 0.08)"
      },
      inputBox: {
        inactive: {
          background: "#f9fafb",
          text: "#1f2937",
          border: "#d1d5db",
          placeholder: "#6b7280"
        },
        active: {
          background: "#ffffff",
          text: "#1f2937",
          border: "#6b7280",
          shadow: "0 0 0 3px rgba(107, 114, 128, 0.1)"
        }
      },
      sendButton: {
        background: "#c7934d",
        text: "#ffffff",
        border: "#c7934d",
        shadow: "0 2px 8px rgba(199, 147, 77, 0.2)",
        hover: {
          background: "#b8843e",
          text: "#ffffff",
          border: "#b8843e",
          shadow: "0 4px 12px rgba(199, 147, 77, 0.3)"
        },
        disabled: {
          background: "#9ca3af",
          text: "#ffffff",
          border: "#9ca3af"
        }
      }
    },
    aiResponseSystem: {
      responseCard: {
        background: "#ffffff",
        border: "#e5e7eb",
        shadow: "0 4px 16px rgba(31, 41, 55, 0.06)",
        borderRadius: "8px",
        padding: {
          default: "32px",
          large: "40px"
        }
      },
      avatar: {
        size: "40px",
        borderRadius: "50%",
        shadow: "0 2px 8px rgba(31, 41, 55, 0.3)",
        border: "none",
        image: {
          objectFit: "cover",
          width: "100%",
          height: "100%"
        }
      },
      contentTypes: createContentTypes("#1f2937", "#6b7280")
    },
    aiAssistant: {
      name: "Madison",
      avatar: {
        size: "32px",
        gradient: {
          start: "#1f2937",
          end: "#c7934d"
        },
        fallback: {
          letter: "M",
          backgroundColor: "#1f2937"
        },
        border: "2px solid #ffffff",
        shadow: "0 2px 8px rgba(31, 41, 55, 0.2)"
      },
      nameStyle: {
        color: "#1f2937",
        fontWeight: "700",
        fontSize: "14px"
      },
      timestamp: {
        color: "#6b7280",
        fontSize: "12px"
      }
    },
    accessibility: {
      focusOutline: "#fbbf24",
      focusOutlineWidth: "3px",
      focusOutlineOffset: "2px",
      minTouchTarget: "44px"
    }
  } as BrandThemeV2,

  'anantara': {
    brand: {
      name: "Anantara",
      colors: {
        primary: "#4a4a4a",
        secondary: "#8b7355",
        cta: "#d4af37",
        ctaText: "#9a7e23",
        gradient: {
          start: "#f5f4f2",
          middle: "#e8e3db",
          end: "#f5f4f2"
        },
        text: {
          primary: "#2d2d2d",
          secondary: "#6d5a43",
          light: "#8b7355",
          disabled: "#9ca3af"
        },
        surface: {
          base: "#FFFFFF",
          subtle: "#FAFAFA",
          warm: "#FEF3E2",
          hover: "#FEF7ED",
          border: "#E0E0E0",
          borderSubtle: "#F0F0F0"
        }
      },
      fonts: {
        primary: "-apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, \"Helvetica Neue\", Arial, sans-serif",
        secondary: "system-ui, -apple-system, 'Segoe UI', sans-serif"
      },
      shadows: {
        light: "0 2px 8px rgba(139, 115, 85, 0.15)",
        medium: "0 4px 16px rgba(139, 115, 85, 0.1)"
      }
    },
    userInterface: {
      topBar: {
        background: "#4a4a4a",
        text: "#ffffff",
        borderBottom: "#6b7280"
      },
      userMessage: {
        background: "#4a4a4a",
        text: "#ffffff",
        border: "#8b7355",
        shadow: "0 2px 8px rgba(139, 115, 85, 0.15)"
      },
      inputBox: {
        inactive: {
          background: "#f5f4f2",
          text: "#2d2d2d",
          border: "#d1d5db",
          placeholder: "#8b7355"
        },
        active: {
          background: "#ffffff",
          text: "#2d2d2d",
          border: "#8b7355",
          shadow: "0 0 0 3px rgba(139, 115, 85, 0.1)"
        }
      },
      sendButton: {
        background: "#d4af37",
        text: "#ffffff",
        border: "#d4af37",
        shadow: "0 2px 8px rgba(212, 175, 55, 0.2)",
        hover: {
          background: "#c4a127",
          text: "#ffffff",
          border: "#c4a127",
          shadow: "0 4px 12px rgba(212, 175, 55, 0.3)"
        },
        disabled: {
          background: "#9ca3af",
          text: "#ffffff",
          border: "#9ca3af"
        }
      }
    },
    aiResponseSystem: {
      responseCard: {
        background: "#ffffff",
        border: "#e8e3db",
        shadow: "0 4px 16px rgba(139, 115, 85, 0.1)",
        borderRadius: "8px",
        padding: {
          default: "32px",
          large: "40px"
        }
      },
      avatar: {
        size: "40px",
        borderRadius: "50%",
        shadow: "0 2px 8px rgba(139, 115, 85, 0.3)",
        border: "none",
        image: {
          objectFit: "cover",
          width: "100%",
          height: "100%"
        }
      },
      contentTypes: createContentTypes("#4a4a4a", "#8b7355")
    },
    aiAssistant: {
      name: "Aria",
      avatar: {
        size: "32px",
        gradient: {
          start: "#d4af37",
          end: "#8b7355"
        },
        fallback: {
          letter: "A",
          backgroundColor: "#d4af37"
        },
        border: "2px solid #ffffff",
        shadow: "0 2px 8px rgba(212, 175, 55, 0.2)"
      },
      nameStyle: {
        color: "#2d2d2d",
        fontWeight: "700",
        fontSize: "14px"
      },
      timestamp: {
        color: "#8b7355",
        fontSize: "12px"
      }
    },
    accessibility: {
      focusOutline: "#fbbf24",
      focusOutlineWidth: "3px",
      focusOutlineOffset: "2px",
      minTouchTarget: "44px"
    }
  } as BrandThemeV2
};

export function getBrandTheme(tenantId: string): BrandTheme {
  const theme = BRAND_THEMES[tenantId] || BRAND_THEMES['zen-pacific-orlando'];

  // Add top-level color aliases for extreme backward compatibility
  return {
    ...theme,
    colors: {
      brandPrimary: theme.brand.colors.primary,
      brandSecondary: theme.brand.colors.secondary,
      brandAccent: theme.brand.colors.cta,
      brandCTA: theme.brand.colors.cta,
      primary: theme.brand.colors.primary,
      secondary: theme.brand.colors.secondary,
      active: theme.brand.colors.cta,
      inactive: theme.brand.colors.inactive || '#9CA3AF',
      accent: theme.brand.colors.accent || theme.brand.colors.cta,
      // v3.0: Use status colors if available, otherwise fallback to defaults
      error: theme.status?.error || '#DC2626',
      warning: theme.status?.warning || '#F59E0B',
      success: theme.status?.success || '#22C55E',
      input: theme.brand.formInputs?.background || '#FFFFFF',
      primaryDark: theme.brand.colors.secondary,
      primaryDarker: '#3A0A11'
    }
  } as any;  // Type assertion for legacy top-level colors property
}